import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Td,
  Tr,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { FaSearch } from "react-icons/fa";
import axios from "axios";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { BLUEMONEDDBUTTONS, WHITE } from "../../../constants/colors";
import { LoggedUser } from "../../../atoms/userLogged";
import ExtratoApi from "../../../services/ExtratoAPI";
import SupervisorClientesApi from "../../../services/supervisorClientesApi";

// Carregamento em duas etapas das listagens de extrato do supervisor.
//
// A rota /Extrato/listagem soma o período inteiro por cliente (função
// ExtratoGeral) e é a consulta mais lenta dessas telas. Antes o operador ficava
// olhando tela vazia até ela responder, mesmo quando só queria clicar em
// Detalhes de um cliente.
//
// Etapa 1: a lista de clientes do supervisor (consulta leve, só nomes) aparece
//          na hora, com o menu de Detalhes já funcionando.
// Etapa 2: os valores chegam e substituem os esqueletos.
//
// A etapa 2 é cancelada quando o usuário sai da tela ou troca de filtro — uma
// consulta pesada cuja resposta ninguém vai mais usar só ocupa banco e conexão.

// colunas de valor entre "Cliente" e "Opções" nas duas listagens
export const COLUNAS_VALOR = 10;

interface FiltrosListagem {
  initialDateFilter: string;
  finalDateFilter: string;
  balanceFilter: string;
  clientFilter?: any;
}

export function useListagemProgressiva(
  filtros: FiltrosListagem,
  idClienteSelecionado: number
) {
  const usuario = useRecoilState(LoggedUser)[0];

  const [clientesBase, setClientesBase] = useState<Array<any>>([]);
  const [valores, setValores] = useState<Array<any>>([]);
  const [valoresCarregados, setValoresCarregados] = useState(false);

  // Etapa 1 — nomes. Não depende do período nem do filtro de saldo, então roda
  // uma vez só e não é refeita a cada mudança de data.
  useEffect(() => {
    let vivo = true;

    async function carregarClientes() {
      if (!usuario?.id) return;

      try {
        const response = await SupervisorClientesApi.getClientesSupervisor(
          usuario.id
        );

        if (!vivo) return;

        const lista = (response ?? [])
          .map((item: any) => item?.cliente)
          .filter((cliente: any) => !!cliente)
          // mesmo formato dos itens da listagem, para a linha ser montada pelo
          // mesmo código nas duas etapas
          .map((cliente: any) => ({
            id: cliente.id,
            isCnpj: cliente.isCnpj,
            razaoSocial: cliente.razaoSocial,
            nome: cliente.nome,
            sobrenome: cliente.sobrenome,
          }))
          // a listagem vem ordenada por razaoSocial no servidor
          .sort((a: any, b: any) =>
            String(a.razaoSocial ?? "").localeCompare(String(b.razaoSocial ?? ""))
          );

        setClientesBase(lista);
      } catch (error) {
        // sem o esqueleto a tela apenas volta a esperar os valores, como antes
        console.log(error);
      }
    }

    carregarClientes();

    return () => {
      vivo = false;
    };
  }, [usuario?.id]);

  // Etapa 2 — valores.
  useEffect(() => {
    const controller = new AbortController();

    async function carregarValores() {
      setValoresCarregados(false);

      try {
        const response: any = await ExtratoApi.getExtractList(
          1,
          100,
          filtros.initialDateFilter,
          filtros.finalDateFilter,
          idClienteSelecionado,
          filtros.balanceFilter,
          controller.signal
        );

        setValores(response ?? []);
        setValoresCarregados(true);
      } catch (error: any) {
        // cancelamento é o caminho esperado ao navegar; não é erro
        if (axios.isCancel(error) || error?.name === "CanceledError") return;
        console.log(error);
        setValoresCarregados(true);
      }
    }

    carregarValores();

    return () => controller.abort();
  }, [
    filtros.clientFilter,
    filtros.initialDateFilter,
    filtros.finalDateFilter,
    filtros.balanceFilter,
    idClienteSelecionado,
  ]);

  // Com filtro de saldo não dá para adiantar as linhas: quem decide se o
  // cliente entra na lista é o saldo, que só existe depois da consulta pesada.
  const podeAdiantar = !valoresCarregados && !filtros.balanceFilter;

  const linhas: Array<any> = valoresCarregados
    ? valores
    : podeAdiantar
    ? // cliente escolhido no cabeçalho restringe a lista, igual à consulta
      clientesBase.filter(
        (cliente) => !idClienteSelecionado || cliente.id === idClienteSelecionado
      )
    : [];

  return { linhas, valoresCarregados };
}

// Linha da etapa 1: nome e menu de Detalhes já utilizáveis, valores em
// esqueleto. `onDetalhes` muda por tela — uma vai para a consulta de
// transações, a outra para o extrato simplificado.
export function LinhaEsqueleto({
  item,
  onDetalhes,
}: {
  item: any;
  onDetalhes: (idCliente: number) => void;
}) {
  return (
    <Tr>
      <Td fontSize={"12px"}>
        {item.isCnpj === true
          ? item.razaoSocial
          : `${item.nome} ${item.sobrenome}`}
      </Td>
      {Array.from({ length: COLUNAS_VALOR }).map((_, coluna) => (
        <Td key={coluna}>
          <Skeleton height="10px" width="60px" />
        </Td>
      ))}
      <Td borderRadius={"0 8px 8px 0"} fontSize="12px">
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<HamburgerIcon />}
            bgColor={BLUEMONEDDBUTTONS}
            color={WHITE}
            size={"sm"}
          />
          <MenuList>
            <MenuItem onClick={() => onDetalhes(item.id)} icon={<FaSearch />}>
              Detalhes
            </MenuItem>
          </MenuList>
        </Menu>
      </Td>
    </Tr>
  );
}
